import { useState, useEffect } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import API from "../../services/api";
import { MessageSquare, Star, User, CalendarDays, HardHat, RefreshCw } from "lucide-react";

const DS = {
    indigo: "#4F46E5", indigoLight: "#EEF2FF", indigoMid: "#6366F1",
    amber: "#D97706", amberLight: "#FFFBEB",
    bg: "#F8FAFC", surface: "#FFFFFF", surfaceAlt: "#F8FAFC",
    border: "#E2E8F0", borderSolid: "#E8E0F5", dark: "#0F172A",
    gray: "#64748B", faint: "#94A3B8", violetFaint: "#F7F3FD",
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

export default function FeedbackLog() {
    const [projects, setProjects] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [feedbackList, setFeedbackList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        API.get("/admin/project-assignments/project-dropdown")
            .then((res) => setProjects(res.data.projects || []))
            .catch((err) => console.error("Projects fetch error:", err));
    }, []);

    const fetchFeedback = async (projectId) => {
        if (!projectId) { setFeedbackList([]); return; }
        setLoading(true);
        try {
            const res = await API.get(`/testimonials/project/${projectId}`);
            setFeedbackList(res.data.data || []);
        } catch (err) {
            console.error("Feedback fetch error:", err);
            setFeedbackList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFeedback(selectedProjectId); }, [selectedProjectId]);

    return (
        <AdminLayout>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap'); * { font-family: 'Montserrat', sans-serif !important; }`}</style>
            <div style={{ padding: "28px 32px", background: DS.bg, minHeight: "100vh" }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                            <div style={{ width: 3, height: 18, borderRadius: 2, background: `linear-gradient(180deg,${DS.indigo},${DS.amber})` }} />
                            <span style={{ fontSize: 11, fontWeight: 700, color: DS.faint, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                                Admin · Feedback Log
                            </span>
                        </div>
                        <h1 style={{ fontWeight: 800, fontSize: 28, color: DS.dark, margin: 0 }}>Client Feedback</h1>
                        <p style={{ color: DS.faint, fontSize: 13, margin: "4px 0 0" }}>Select a project to view all client feedback submitted for it</p>
                    </div>
                    <button
                        onClick={async () => { setRefreshing(true); await fetchFeedback(selectedProjectId); setRefreshing(false); }}
                        disabled={refreshing || !selectedProjectId}
                        style={{
                            width: 40, height: 40, borderRadius: 11, border: `1.5px solid ${DS.borderSolid}`,
                            background: DS.surface, color: DS.gray, display: "flex", alignItems: "center",
                            justifyContent: "center", cursor: !selectedProjectId ? "not-allowed" : "pointer",
                            opacity: !selectedProjectId ? 0.5 : 1,
                        }}
                        title="Refresh"
                    >
                        <RefreshCw size={16} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
                    </button>
                </div>

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

                {/* Filter by Project */}
                <div style={{
                    background: DS.surface, borderRadius: 16, border: `1.5px solid ${DS.borderSolid}`,
                    padding: "18px 24px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <HardHat size={17} color={DS.indigo} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: DS.dark }}>Select Project</div>
                            <div style={{ fontSize: 11, color: DS.faint, marginTop: 1 }}>Choose a project to view its feedback</div>
                        </div>
                    </div>
                    <select
                        value={selectedProjectId}
                        onChange={(e) => setSelectedProjectId(e.target.value)}
                        style={{
                            flex: 1, minWidth: 240, maxWidth: 400, borderRadius: 10,
                            border: `1.5px solid ${DS.borderSolid}`, padding: "10px 14px",
                            fontSize: 13, color: DS.dark, background: DS.surfaceAlt, outline: "none", cursor: "pointer",
                        }}
                    >
                        <option value="">— Select a project —</option>
                        {projects.map((p) => (
                            <option key={p.id} value={p.id}>{p.project || p.title || `Project ${p.id}`}</option>
                        ))}
                    </select>
                </div>

                {/* Feedback List */}
                <div style={{ background: DS.surface, borderRadius: 20, border: `1px solid ${DS.borderSolid}`, overflow: "hidden", boxShadow: "0 4px 24px rgba(79,70,229,0.08)" }}>
                    <div style={{ height: 4, background: `linear-gradient(90deg,${DS.indigo},${DS.amber})` }} />
                    <div style={{ padding: "20px 24px" }}>
                        {!selectedProjectId ? (
                            <div style={{ textAlign: "center", padding: "60px 20px", color: DS.faint }}>
                                <MessageSquare size={40} color={DS.faint} style={{ marginBottom: 12 }} />
                                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark, marginBottom: 6 }}>No project selected</div>
                                <div style={{ fontSize: 13 }}>Choose a project above to see its feedback.</div>
                            </div>
                        ) : loading ? (
                            <div style={{ textAlign: "center", padding: "60px 20px", color: DS.faint, fontSize: 13 }}>Loading feedback…</div>
                        ) : feedbackList.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "60px 20px", color: DS.faint }}>
                                <MessageSquare size={40} color={DS.faint} style={{ marginBottom: 12 }} />
                                <div style={{ fontWeight: 700, fontSize: 15, color: DS.dark, marginBottom: 6 }}>No feedback yet</div>
                                <div style={{ fontSize: 13 }}>This project has no client feedback submitted.</div>
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {feedbackList.map((f) => (
                                    <div key={f.id} style={{
                                        border: `1px solid ${DS.borderSolid}`, borderRadius: 14, padding: "16px 18px",
                                        background: DS.surfaceAlt,
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                <div style={{ width: 30, height: 30, borderRadius: "50%", background: DS.indigoLight, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                    <User size={14} color={DS.indigo} />
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: 13, color: DS.dark }}>{f.client_name || "Client"}</span>
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <div style={{ display: "flex", gap: 2 }}>
                                                    {[1,2,3,4,5].map((n) => (
                                                        <Star key={n} size={14} fill={n <= (f.rating || 0) ? DS.amber : "none"} color={DS.amber} />
                                                    ))}
                                                </div>
                                                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: DS.gray }}>
                                                    <CalendarDays size={12} /> {formatDate(f.created_at)}
                                                </div>
                                            </div>
                                        </div>
                                        <p style={{ margin: 0, fontSize: 13, color: DS.dark, lineHeight: 1.6 }}>{f.feedback}</p>
                                        {f.category && (
                                            <span style={{ display: "inline-block", marginTop: 8, fontSize: 10, fontWeight: 700, color: DS.indigo, background: DS.indigoLight, padding: "3px 10px", borderRadius: 12 }}>
                                                {f.category}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}